import { useCallback, useMemo } from 'react';

import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useTableFilters } from '@/components/data-table/filters-v2';
import { getNextPageParam } from '@/lib/constants/pagination';
import { DateRangeV2 } from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { mapCustomAttributeFilters } from '@/modules/common/utils/custom-attribute-filters';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';
import { normalizeToEndOfDayUTC } from '@/utils/date-formatters/date-formatters';

import { getApprovalProjects, getApprovalSummary } from '../api/time-tracking.api';
import {
  APPROVALS_FILTER_KEY_DEPARTMENT_IDS,
  APPROVALS_FILTER_KEY_MEMBER_IDS,
  APPROVALS_FILTER_KEY_PROJECT_IDS,
  APPROVALS_FILTER_KEY_SEARCH,
  APPROVALS_FILTER_KEY_STATUSES,
} from '../constants/approvals-filter-keys';
import { getApprovalsFiltersConfig } from '../constants/approvals-filters-config';
import { APPROVALS_QUERY_KEYS } from '../constants/approvals-query-keys';
import { ApprovalProjectRow } from '../types/time-tracking-approvals';

const APPROVALS_STALE_TIME = 1 * 60 * 1000;

export const useApprovals = (dates: DateRangeV2 | null) => {
  const { data: customAttributes } = useCustomAttributes();
  const filtersConfig = useMemo(
    () => getApprovalsFiltersConfig(customAttributes),
    [customAttributes]
  );
  const filterState = useTableFilters(filtersConfig);

  const projectIds = filterState.getFilterValue(APPROVALS_FILTER_KEY_PROJECT_IDS).map(Number);
  const userIds = filterState.getFilterValue(APPROVALS_FILTER_KEY_MEMBER_IDS).map(Number);
  const departmentIds = filterState.getFilterValue(APPROVALS_FILTER_KEY_DEPARTMENT_IDS).map(Number);
  const statuses = filterState.getFilterValue(APPROVALS_FILTER_KEY_STATUSES);
  const [search] = filterState.getFilterValue(APPROVALS_FILTER_KEY_SEARCH);

  const dateFilter = useMemo(
    () =>
      dates
        ? {
            startDate: normalizeToStartOfDayUTC(dates.startDate).toISOString(),
            endDate: normalizeToEndOfDayUTC(dates.endDate).toISOString(),
          }
        : null,
    [dates?.startDate.getTime(), dates?.endDate.getTime()]
  );

  const customAttributeFilters = useMemo(
    () => mapCustomAttributeFilters(filterState.activeFilters),
    [filterState.activeFilters]
  );

  const activeFilter = useMemo(
    () =>
      dateFilter
        ? {
            ...dateFilter,
            ...(projectIds.length ? { projectIds } : {}),
            ...(userIds.length ? { userIds } : {}),
            ...(departmentIds.length ? { departmentIds } : {}),
            ...(statuses.length ? { statuses } : {}),
            ...(search ? { search } : {}),
            ...(customAttributeFilters ? { customAttributeFilters } : {}),
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dateFilter,
      projectIds.join(','),
      userIds.join(','),
      departmentIds.join(','),
      statuses.join(','),
      search,
      customAttributeFilters,
    ]
  );

  const {
    data: projectsData,
    isLoading,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // Cursor lives in `pageParam`, never the query key (see NESTED-PAGINATION.md).
    queryKey: APPROVALS_QUERY_KEYS.projects(activeFilter!),
    queryFn: ({ pageParam }) => getApprovalProjects(activeFilter!, pageParam),
    getNextPageParam,
    initialPageParam: undefined as number | null | undefined,
    enabled: !!activeFilter,
    staleTime: APPROVALS_STALE_TIME,
    // Filter changes keep the previous rows on screen (no full-table spinner /
    // layout jump) and swap them in place once the new page arrives.
    placeholderData: keepPreviousData,
    // Flatten + dedupe by row id, keeping the first-seen object so project row
    // references stay stable across page appends. `totalWorklogs` is an aggregate
    // identical across pages — read it from the first page.
    select: data => {
      const seen = new Map<string, ApprovalProjectRow>();
      for (const page of data.pages) {
        for (const row of page.items) {
          if (!seen.has(row.id)) seen.set(row.id, row);
        }
      }
      return {
        rows: Array.from(seen.values()),
        totalWorklogs: data.pages[0]?.totalWorklogs ?? 0,
      };
    },
  });

  const fetchMoreProjects = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: APPROVALS_QUERY_KEYS.summary(activeFilter!),
    queryFn: () => getApprovalSummary(activeFilter!),
    enabled: !!activeFilter,
    staleTime: APPROVALS_STALE_TIME,
  });

  const totalWorklogs = projectsData?.totalWorklogs ?? 0;

  // Block the table with a full spinner ONLY when there is no data yet — i.e. the very
  // first load. `placeholderData: keepPreviousData` above means a later filter change
  // keeps `isLoading` false (existing rows stay mounted while the new page fetches in
  // the background) — so this must be the PROJECTS `isLoading` alone. It intentionally
  // does NOT include `isSummaryLoading`: the summary cards render their own skeleton
  // independently (and, unlike the projects query, are not given `placeholderData`, so
  // they legitimately reload on every filter change) — the table must not wait on them.
  return {
    data: projectsData?.rows ?? null,
    isLoading,
    isSummaryLoading,
    isWorklogsLoading: isLoading,
    /** True during any projects refetch (incl. filter changes served from placeholder data). */
    isFetchingProjects: isFetching,
    summary,
    totalWorklogs,
    filtersConfig,
    filterState,
    filter: activeFilter,
    // Top-level (projects) cursor pagination, driven by the table's scroll observer.
    fetchMoreProjects,
    hasMoreProjects: hasNextPage,
    isFetchingMoreProjects: isFetchingNextPage,
  };
};
